import SiteCoreLinks from "@/components/SiteCoreLinks";
import { createFileRoute, Link } from "@tanstack/react-router";
import { FileText, Phone, MessageCircle } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "ข้อกำหนดและเงื่อนไขการใช้บริการ | NYC Language Institute";
const DESC =
  "ข้อกำหนดและเงื่อนไขการให้บริการแปลเอกสาร รับรองโนตารี รับรองกระทรวงการต่างประเทศ NAATI และวีซ่า ของ NYC Legal & Notary Service Co., Ltd. คุยกับผู้เชี่ยวชาญตัวจริง ตอบกลับภายในไม่กี่นาที";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/terms", "th")},
      { name: "description", content: ctaDesc(DESC, "/terms", "th")},
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: ctaTitle(TITLE, "/terms", "th")},
      { property: "og:description", content: ctaDesc(DESC, "/terms", "th")},
      { property: "og:url", content: `${SITE}/terms` },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/terms` },
      ...hreflangLinks("/terms"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: TITLE,
          description: DESC,
          url: `${SITE}/terms`,
          inLanguage: "th-TH",
          isPartOf: { "@type": "WebSite", name: "NYC Language Institute", url: SITE },
          about: { "@type": "Thing", name: "Terms of Service" },
        }),
      },
    ],
  }),
  component: TermsPage,
});

const SECTIONS: { h: string; body: string[] }[] = [
  {
    h: "1. การยอมรับข้อกำหนด",
    body: [
      "การใช้บริการของ NYC Legal & Notary Service Co., Ltd. (\"บริษัท\") ไม่ว่าจะเป็นการสั่งงานทางเว็บไซต์ LINE อีเมล โทรศัพท์ หรือที่สำนักงาน ถือว่าท่านยอมรับและตกลงตามข้อกำหนดและเงื่อนไขฉบับนี้",
      "หากท่านไม่ยอมรับข้อกำหนด กรุณางดใช้บริการของบริษัท",
    ],
  },
  {
    h: "2. ขอบเขตการให้บริการ",
    body: [
      "บริการแปลเอกสาร 50+ ภาษา",
      "บริการรับรองโดยทนาย Notarial Services Attorney (Notary Public)",
      "บริการรับรองกระทรวงการต่างประเทศ (MFA) และสถานทูตทั่วโลก",
      "บริการแปลรับรอง NAATI (Australia)",
      "บริการขอวีซ่า ใบอนุญาตทำงาน และจดทะเบียนนิติบุคคล",
      "การให้คำปรึกษาทางกฎหมายเฉพาะที่เกี่ยวข้องกับบริการข้างต้น",
    ],
  },
  {
    h: "3. การสั่งงานและการยืนยัน",
    body: [
      "ลูกค้าต้องส่งเอกสารต้นฉบับหรือสำเนาที่ชัดเจนพร้อมข้อมูลที่ครบถ้วน",
      "ใบเสนอราคามีอายุ 7 วันนับจากวันที่ออก",
      "การสั่งงานจะถือว่าสมบูรณ์เมื่อบริษัทได้รับการยืนยันเป็นลายลักษณ์อักษร (LINE/อีเมล) และ/หรือเงินมัดจำตามที่ตกลง",
    ],
  },
  {
    h: "4. ราคา การชำระเงิน และใบกำกับภาษี",
    body: [
      "ราคาเป็นไปตามใบเสนอราคาที่ตกลงกันก่อนเริ่มงาน รวมภาษีมูลค่าเพิ่ม 7% (เว้นแต่ระบุไว้เป็นอย่างอื่น)",
      "วิธีชำระเงิน: โอนผ่านธนาคาร, PromptPay, บัตรเครดิต/เดบิต, QR Code",
      "งานที่มูลค่ามากกว่า ฿5,000 อาจต้องวางมัดจำ 50% ก่อนเริ่มงาน",
      "บริษัทออกใบกำกับภาษีอิเล็กทรอนิกส์ (e-Tax Invoice) ตามมาตรฐานกรมสรรพากร",
      "หากชำระล่าช้าเกิน 30 วัน บริษัทขอสงวนสิทธิ์คิดดอกเบี้ย 1.5% ต่อเดือน",
    ],
  },
  {
    h: "5. ระยะเวลาดำเนินงาน",
    body: [
      "ระยะเวลาที่ระบุในใบเสนอราคาเป็นวันทำการ ไม่นับเสาร์-อาทิตย์และวันหยุดราชการ",
      "ระยะเวลารับรอง MFA/สถานทูต ขึ้นกับคิวของหน่วยงานราชการ ซึ่งอยู่นอกเหนือการควบคุมของบริษัท",
      "งานเร่งด่วน (Express/Same-day) มีค่าบริการเพิ่มตามอัตราที่ตกลง",
    ],
  },
  {
    h: "6. การรับประกันคุณภาพ",
    body: [
      "บริษัทรับประกันคุณภาพการแปลและการรับรองตามมาตรฐานวิชาชีพ ISO 17100",
      "หากเอกสารถูกปฏิเสธจากหน่วยงานผู้รับเนื่องจากความผิดพลาดในการแปล/รับรองที่เกิดจากบริษัท เราจะแก้ไขให้ใหม่โดยไม่คิดค่าใช้จ่ายและออกค่าธรรมเนียมที่เกี่ยวข้องให้",
      "ลูกค้าต้องตรวจทานเอกสารและแจ้งข้อผิดพลาด/แก้ไขภายใน 7 วันนับจากวันส่งมอบ",
    ],
  },
  {
    h: "7. ข้อจำกัดความรับผิด",
    body: [
      "บริษัทไม่รับผิดชอบความเสียหายทางอ้อม ความเสียหายต่อเนื่อง หรือการสูญเสียโอกาสทางธุรกิจ",
      "ความรับผิดสูงสุดของบริษัทไม่เกินมูลค่าค่าบริการของงานที่เกี่ยวข้อง",
      "บริษัทไม่รับผิดชอบกรณีลูกค้าให้ข้อมูลไม่ถูกต้อง/ไม่ครบถ้วน หรือเอกสารต้นฉบับเป็นเอกสารปลอม",
      "ผลของการพิจารณาวีซ่า/ใบอนุญาตเป็นอำนาจของเจ้าหน้าที่หน่วยงานราชการ บริษัทดำเนินงานในฐานะตัวแทนจัดเตรียม/ยื่นเอกสารเท่านั้น",
    ],
  },
  {
    h: "8. การยกเลิกงานและการคืนเงิน",
    body: [
      "ยกเลิกก่อนเริ่มงาน: คืนเงินเต็มจำนวน",
      "ยกเลิกหลังเริ่มงานแต่ยังไม่ส่งมอบ: คิดค่าบริการตามสัดส่วนงานที่ทำไปแล้ว",
      "งานที่ส่งมอบเสร็จแล้วไม่สามารถยกเลิกหรือขอคืนเงินได้",
      "ค่าธรรมเนียมที่ชำระให้หน่วยงานราชการ (MFA, สถานทูต, ตม.) ไม่สามารถขอคืนได้",
    ],
  },
  {
    h: "9. ทรัพย์สินทางปัญญาและการเก็บรักษาเอกสาร",
    body: [
      "เอกสารที่ลูกค้าส่งมาแปลยังคงเป็นทรัพย์สินของลูกค้า",
      "บริษัทเก็บรักษาสำเนาเอกสารแปลเป็นเวลา 7 ปี เพื่อให้บริการขอสำเนาเพิ่มเติมในภายหลัง",
      "ลูกค้ามีสิทธิ์ขอลบข้อมูลก่อนกำหนดได้ ภายใต้ข้อยกเว้นทางกฎหมาย (เช่น AMLA ต้องเก็บ 10 ปี)",
    ],
  },
  {
    h: "10. การรักษาความลับ",
    body: [
      "บริษัทและทีมงานทุกคนลงนามใน NDA และมีหน้าที่รักษาความลับของข้อมูลลูกค้าตลอดอายุการให้บริการและภายหลังสิ้นสุดสัญญา",
      "บริษัทจะไม่เปิดเผยข้อมูลลูกค้าแก่บุคคลที่สาม เว้นแต่ได้รับความยินยอมหรือตามคำสั่งศาล/หน่วยงานราชการ",
    ],
  },
  {
    h: "11. เหตุสุดวิสัย (Force Majeure)",
    body: [
      "บริษัทไม่รับผิดชอบความล่าช้าหรือไม่สามารถดำเนินงานได้เนื่องจากเหตุสุดวิสัย เช่น ภัยธรรมชาติ การประท้วง สงคราม การประกาศปิดหน่วยงานราชการ ระบบเทคโนโลยีล่ม ฯลฯ",
    ],
  },
  {
    h: "12. กฎหมายที่ใช้บังคับและการระงับข้อพิพาท",
    body: [
      "ข้อกำหนดฉบับนี้อยู่ภายใต้บังคับของกฎหมายไทย",
      "หากเกิดข้อพิพาท คู่สัญญาจะร่วมเจรจาด้วยความสุจริตก่อน หากไม่สามารถตกลงได้ภายใน 60 วัน ให้นำคดีขึ้นสู่ศาลที่มีเขตอำนาจในกรุงเทพมหานคร",
    ],
  },
  {
    h: "13. การแก้ไขข้อกำหนด",
    body: [
      "บริษัทขอสงวนสิทธิ์ในการแก้ไขข้อกำหนดได้ทุกเมื่อ โดยจะประกาศการแก้ไขผ่านเว็บไซต์ การใช้บริการต่อหลังการแก้ไขถือเป็นการยอมรับข้อกำหนดฉบับใหม่",
    ],
  },
];

function TermsPage() {
  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-4xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5 text-xs font-semibold text-amber-300">
            <FileText className="h-3.5 w-3.5" /> Terms of Service · ข้อกำหนดการใช้บริการ
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-5xl">
            ข้อกำหนดและเงื่อนไขการใช้บริการ
          </h1>
          <p className="mt-3 max-w-2xl text-white/75">
            กรุณาอ่านข้อกำหนดฉบับนี้อย่างละเอียดก่อนใช้บริการ การใช้บริการของบริษัทถือว่าท่านยอมรับข้อกำหนดทั้งหมด
          </p>
          <p className="mt-2 text-xs text-white/80">มีผล: 1 มกราคม 2569 · ปรับปรุงล่าสุด: พฤษภาคม 2569</p>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-4xl space-y-6">
          {SECTIONS.map((s) => (
            <article key={s.h} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
              <h2 className="text-lg font-bold text-amber-300 sm:text-xl">{s.h}</h2>
              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-white/80">
                {s.body.map((line, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-amber-400" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}

          <article className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-6 sm:p-8">
            <h2 className="text-lg font-bold sm:text-xl">มีคำถามเกี่ยวกับข้อกำหนด?</h2>
            <p className="mt-3 text-sm text-white/80">ทีมกฎหมายของเรายินดีตอบทุกคำถามก่อนเริ่มใช้บริการ</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400">
                <Phone className="h-4 w-4" /> 083-249-4999
              </a>
              <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-[#04A03A] px-5 py-3 text-sm font-bold text-white hover:bg-[#05B14C]">
                <MessageCircle className="h-4 w-4" /> LINE @nycli
              </a>
            </div>
            <p className="mt-5 text-xs text-white/80">
              อ่านเพิ่ม: <Link to="/privacy" className="underline hover:text-amber-300">นโยบายความเป็นส่วนตัว (PDPA)</Link> ·{" "}
              <Link to="/trust" className="underline hover:text-amber-300">ความน่าเชื่อถือ</Link> ·{" "}
              <Link to="/faq" className="underline hover:text-amber-300">FAQ</Link>
            </p>
          </article>
        </div>
      </section>
          <SiteCoreLinks />
    </main>
  );
}
